import { DATAPOINT, DERIVED_CONTENT, HEADING, REPEATER } from '@components/SelectMenu/constants';
import type { SelectMenuItem } from '@components/SelectMenu/TreeView';
import { CLAUSE_TYPES } from '@root/helpers/clauses';
import { getChildrenWithPath, resolveSchema } from '@root/helpers/schema';
import { isContentRepeater, isRepeater } from '@root/helpers/schema/resolveSchemaTreeFromForm';
import { cloneDeep } from 'lodash-es';
import pluralize from 'pluralize';

type BasicSchema = {
  id: string;
  qid?: number;
  title?: string;
  'ui:component'?: string;
  items?: any;
  'ui:hidden'?: boolean;
};

const clauseListItems: SelectMenuItem = {
  id: 'clause-list',
  type: HEADING,
  order: 7,
  label: 'Derived content',
  badgeLabel: '',
  children: CLAUSE_TYPES.map((type, order) => ({
    id: `list_of_${type.toLocaleLowerCase().split(' ').join('_')}`,
    type: DERIVED_CONTENT,
    order,
    label: `List of ${pluralize(type)}`,
    badgeLabel: '',
    replacementClasses: 'cursor-pointer gap-x-3.5 px-6 py-2 transition-colors hover:bg-primary-50',
  })),
};

export const getItemsFromSchema = (schema: BasicSchema[]): SelectMenuItem[] => {
  return schema
    .filter((item) => !item['ui:hidden'])
    .map((item) => {
      const label = isContentRepeater(item['ui:component']) ? item?.items?.title : item.title;
      const isRepeaterInserter = isRepeater(item['ui:component']);
      const badgeLabel = isContentRepeater(item['ui:component']) ? 'Repeater' : 'Section';
      const children = getChildrenWithPath(item, item.id);

      return {
        label: label ?? '',
        type: isRepeaterInserter ? REPEATER : DATAPOINT,
        id: item.id!,
        order: item.qid ?? Infinity,
        badgeLabel: isRepeaterInserter ? badgeLabel : undefined,
        disableInsert: false,
        payload: item,
        children: children ? getItemsFromSchema(children) : [],
      };
    });
};

export const getSelectMenuItemsNoClauses = (schema: any): SelectMenuItem[] => {
  const resolvedSchema = resolveSchema(cloneDeep(schema), {}, true);
  const roots = Object.entries(resolvedSchema?.properties?.SubmissionForm?.properties).map(
    ([key, value]: [string, any]) => ({ ...value, id: key }),
  );

  return [
    {
      id: 'datapoints-list',
      type: HEADING,
      order: 0,
      label: 'Data points',
      badgeLabel: '',
      children: getItemsFromSchema(roots),
    },
  ];
};

export const getSelectMenuItemsForContracts = (schema: any): SelectMenuItem[] => {
  return getSelectMenuItemsNoClauses(schema).concat(clauseListItems);
};
